/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { FunctionComponent } from 'preact';
import { HTMLAttributes, useCallback, useEffect, useRef } from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Close as CloseSVG } from '@adobe-commerce/elsie/icons';
import { Button } from '@adobe-commerce/elsie/components';

import '@/pdp/components/Overlay/Overlay.css';

export interface OverlayProps extends HTMLAttributes<HTMLDivElement> {
  centered?: boolean;
  onClose?: () => void;
}

export const Overlay: FunctionComponent<OverlayProps> = ({
  centered,
  onClose,
  className,
  children,
  ...props
}) => {
  // i18n
  const CloseLabel = useText('PDP.Overlay.Close.label').label;
  const overlayRef = useRef<HTMLInputElement>(null);
  const handleOnClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  // Close on ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleOnClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleOnClose]);

  // Prevent document scrolling
  useEffect(() => {
    const input = overlayRef.current;
    input?.focus();

    const scrollingElement = document.scrollingElement as HTMLElement;

    const defaultOverflowValue = scrollingElement.style.overflow;

    scrollingElement.style.overflow = 'hidden';

    return () => {
      scrollingElement.style.overflow = defaultOverflowValue;
    };
  }, []);

  return (
    <div
      {...props}
      className={classes([
        'pdp-overlay',
        ['pdp-overlay--centered', centered],
        className,
      ])}
      ref={overlayRef}
      role="dialog"
      aria-describedby="overlay-content"
      tabindex={0}
    >
      <div className="pdp-overlay__content" id="overlay-content">
        {children}
      </div>
      <Button
        aria-label={CloseLabel}
        variant="tertiary"
        className="pdp-overlay__close-button"
        onClick={handleOnClose}
        icon={<CloseSVG />}
      />
    </div>
  );
};
